import { useState } from "react";
import { Volume2, Play, Sparkles, Film } from "lucide-react";
import AnimatedBackground from "@/components/AnimatedBackground";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionHeader from "@/components/SectionHeader";

type Category = "imagefilme" | "ki-videos";

const VideoShowcase = () => {
  const [category, setCategory] = useState<Category>("imagefilme");
  const [activeVideo, setActiveVideo] = useState(0);
  const [videoWithSound, setVideoWithSound] = useState(false);
  const isMobile = useIsMobile();

  const handleVideoClick = () => {
    setVideoWithSound(true);
  };

  const handleVideoChange = (index: number) => {
    setActiveVideo(index);
    setVideoWithSound(false);
  };

  const handleCategoryChange = (cat: Category) => {
    setCategory(cat);
    setActiveVideo(0);
    setVideoWithSound(false);
  };

  const categories = {
    imagefilme: {
      label: "Imagefilme & Testimonials",
      icon: Film,
      videos: [
        { id: "1089155100", title: "TMD Friction Services GmbH", mobileTitle: "TMD Friction" },
        { id: "1065378871", title: "A-Z Dellen- und Hagelschadenreparatur GmbH", mobileTitle: "Dellendoktor" },
        { id: "1137933071", title: "Jofo Pneumatik GmbH", mobileTitle: "Pneumatik" },
      ],
    },
    "ki-videos": {
      label: "KI-Videos & Reels",
      icon: Sparkles,
      videos: [
        { id: "1137892262", title: "FIRMENLADEN GmbH", mobileTitle: "Firmenladen" },
        { id: "1137900417", title: "Hammer GmbH & Co. KG, Advanced Logistics", mobileTitle: "Hammer" },
        { id: "1137901108", title: "Janssen Cosmetics GmbH", mobileTitle: "Janssen" },
      ],
    },
  };

  const currentCategory = categories[category];
  const currentVideo = currentCategory.videos[activeVideo];
  const isKI = category === "ki-videos";
  // Janssen video is vertical (9:16)
  const isVertical = isKI && activeVideo === 2;

  return (
    <section id="work" className="py-12 md:py-20 lg:py-24 bg-gradient-to-b from-background/90 via-background/95 to-background relative overflow-hidden">
      <AnimatedBackground variant={isKI ? "blue" : "green"} />
      {isKI && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.08),transparent_50%)]"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(0deg, rgba(59,130,246,0.5) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </>
      )}

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <SectionHeader
          badge="Unsere Produktionen"
          title="Mehr Sichtbarkeit für Ihr Unternehmen"
          highlightedWord="Sichtbarkeit"
          description="Ob Imagefilm, Testimonial oder KI-Video – wir produzieren Videos, die Kunden überzeugen, Bewerber anziehen und Vertrauen aufbauen."
          className="mb-8 md:mb-12"
        />

        {/* Category Switcher */}
        <div className="flex justify-center mb-8 md:mb-10">
          <div className="inline-flex items-center bg-muted/50 backdrop-blur-sm rounded-full p-1 border border-border/30">
            {(Object.entries(categories) as [Category, typeof categories.imagefilme][]).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => handleCategoryChange(key)}
                className={`inline-flex items-center gap-2 px-5 md:px-6 py-2.5 text-sm rounded-full transition-all duration-300 ${
                  category === key
                    ? key === "ki-videos"
                      ? "bg-blue-500 text-white font-semibold shadow-lg shadow-blue-500/30"
                      : "bg-accent text-background font-semibold shadow-lg"
                    : "text-foreground/70 md:hover:text-foreground"
                }`}
              >
                <cat.icon className="w-4 h-4" />
                <span className="hidden md:inline">{cat.label}</span>
                <span className="md:hidden">{key === "imagefilme" ? "Imagefilme" : "KI-Videos"}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Video Player */}
        <div className="max-w-5xl mx-auto mb-6 md:mb-8 px-4 md:px-0">
          <div
            className={`${isVertical ? 'aspect-[9/16] max-w-md' : 'aspect-video'} bg-muted rounded-lg overflow-hidden shadow-2xl ${isKI ? 'shadow-blue-500/20' : 'shadow-accent/20'} animate-fade-in mx-auto group cursor-pointer relative`}
            onClick={handleVideoClick}
          >
            {/* Thumbnail Fallback */}
            <div
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${(isMobile && !videoWithSound) ? 'opacity-100' : (!isMobile ? 'opacity-0' : 'opacity-0')}`}
              style={{ backgroundImage: `url(https://vumbnail.com/${currentVideo.id}.jpg)` }}
            />
            {(!isMobile || videoWithSound) && (
              <iframe
                key={`${category}-${activeVideo}-${videoWithSound ? 'sound' : 'muted'}`}
                src={`https://player.vimeo.com/video/${currentVideo.id}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&loop=1&muted=${videoWithSound ? '0' : '1'}&dnt=1`}
                className="w-full h-full relative z-10"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title={`${isKI ? 'KI-Videoproduktion' : 'Imagefilm'} Aachen - ${currentVideo.title}`}
              />
            )}
            {!videoWithSound && (
              <div className={`absolute inset-0 flex items-center justify-center z-20 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-300`}>
                <div className="bg-background/80 backdrop-blur-sm rounded-full p-4">
                  {isMobile
                    ? <Play className={`w-6 h-6 ${isKI ? 'text-blue-400 fill-blue-400' : 'text-accent fill-accent'}`} />
                    : <Volume2 className={`w-6 h-6 ${isKI ? 'text-blue-400' : 'text-accent'}`} />
                  }
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Selector */}
        <div className="flex justify-center animate-fade-in px-4">
          <div className={`inline-flex items-center backdrop-blur-sm rounded-full p-1 border ${isKI ? 'bg-blue-500/10 border-blue-500/30' : 'bg-muted/50 border-accent/20'}`}>
            {currentCategory.videos.map((video, index) => (
              <button
                key={index}
                onClick={() => handleVideoChange(index)}
                className={`relative px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm rounded-full transition-all duration-300 ${
                  activeVideo === index
                    ? isKI
                      ? "bg-blue-500 text-white font-semibold shadow-lg shadow-blue-500/30"
                      : "bg-accent text-background font-semibold shadow-lg"
                    : "text-foreground/70 md:hover:text-foreground"
                }`}
              >
                <span className="md:hidden">{video.mobileTitle}</span>
                <span className="hidden md:inline">{video.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoShowcase;
